import { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, Stack, Typography } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlaceIcon from "@mui/icons-material/Place";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import { calendarService, liveMatchService } from "../../api/service.ts";
import type { NextMatchInfo } from "../../api/types";
import MatchReplayDialog from "../LiveMatch/replay/MatchReplayDialog.tsx";
import MapsButton from "../MapsButton.tsx";
import TravelChip from "../TravelChip.tsx";

const POLL_MS = 30_000;

function daysUntil(iso: string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(iso);
    d.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

function whenLabel(iso: string): string {
    const n = daysUntil(iso);
    if (n <= 0) return "Oggi!";
    if (n === 1) return "Domani";
    return `Tra ${n} giorni`;
}

/**
 * Prossima partita della stagione corrente oppure, se c'è una partita in corso, badge LIVE
 * con accesso alla vista spettatore. Aggiornata ogni 30 secondi.
 */
export default function NextMatchCard() {
    const [next, setNext] = useState<NextMatchInfo | null>(null);
    const [liveEventId, setLiveEventId] = useState<number | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [watching, setWatching] = useState(false);

    const refresh = useCallback(async () => {
        const live = await liveMatchService.getActive();
        setLiveEventId(live ? live.matchEventId : null);
        setNext(await calendarService.getNextMatch());
        setLoaded(true);
    }, []);

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, POLL_MS);
        return () => clearInterval(t);
    }, [refresh]);

    if (!loaded) return null;

    if (liveEventId !== null) {
        return (
            <>
                <Card elevation={4} sx={{ borderRadius: 4, p: 2, bgcolor: "#1a1a2e", color: "white" }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                        <Stack direction="row" alignItems="center" gap={1.5}>
                            <Box
                                sx={{
                                    px: 1.2, py: 0.3, bgcolor: "error.main", borderRadius: 1, fontSize: 13, fontWeight: 700, letterSpacing: 1,
                                    animation: "livePulse 1.5s infinite",
                                    "@keyframes livePulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.5 } },
                                }}
                            >
                                LIVE
                            </Box>
                            <Typography sx={{ fontWeight: 700 }}>Partita in corso</Typography>
                        </Stack>
                        <Button variant="contained" color="error" size="small" startIcon={<LiveTvIcon />} onClick={() => setWatching(true)}>
                            Segui live
                        </Button>
                    </Stack>
                </Card>
                {watching && (
                    <MatchReplayDialog open liveMode eventId={liveEventId} onClose={() => setWatching(false)} />
                )}
            </>
        );
    }

    if (!next) {
        return (
            <Card sx={{ borderRadius: 4, p: 2, color: "text.secondary" }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <EventIcon fontSize="small" />
                    <Typography variant="body2">Nessun incontro in programma</Typography>
                </Stack>
            </Card>
        );
    }

    const date = new Date(next.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
    const soon = daysUntil(next.date) <= 1;

    return (
        <Card
            elevation={4}
            sx={{
                borderRadius: 4,
                p: 2,
                color: "white",
                background: "linear-gradient(135deg, #1565c0 0%, #1976d2 55%, #42a5f5 100%)",
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="overline" sx={{ opacity: 0.85, lineHeight: 1.5 }}>
                    Prossimo incontro
                    {next.competition && ` · ${next.competition}`}
                    {next.groupName && ` · Girone ${next.groupName}`}
                </Typography>
                <Box sx={{ px: 1, py: 0.2, borderRadius: 1, bgcolor: soon ? "warning.main" : "rgba(255,255,255,0.2)", fontSize: 12, fontWeight: 700 }}>
                    {whenLabel(next.date)}
                </Box>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                {next.homeTeam} <Box component="span" sx={{ opacity: 0.7, fontWeight: 400 }}>vs</Box> {next.awayTeam}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} gap={{ xs: 0.5, sm: 2 }} sx={{ mt: 1, opacity: 0.95 }}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <EventIcon fontSize="small" />
                    <Typography variant="body2" sx={{ textTransform: "capitalize" }}>{date}</Typography>
                </Stack>
                {next.time && (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <AccessTimeIcon fontSize="small" />
                        <Typography variant="body2">{next.time.substring(0, 5)}</Typography>
                    </Stack>
                )}
                {next.location && (
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                        <PlaceIcon fontSize="small" />
                        <Typography variant="body2" noWrap>{next.location}</Typography>
                        <MapsButton address={next.location} variant="icon" color="inherit" />
                    </Stack>
                )}
            </Stack>
            {next.travel && (
                <Box sx={{ mt: 1 }}>
                    <TravelChip travel={next.travel} tone="light" />
                </Box>
            )}
        </Card>
    );
}
